import { AuthState, ErrorHandling, RBAC } from '@pulseops/common'
import { statusStyles } from '../styles/policyDispatchingStyles'
import { pipe } from 'fp-ts/lib/function'

const mapStatus = {
  AVAILABLE: 'Còn trong kho',
  ISSUED: 'Nhận từ NB',
  IN_USED: 'Đã giao',
  RETURNED: 'Đã trả lại'
}

const mapScreen = {
  'Stock in': 'Nhập kho',
  'Stock out Agent': 'Giao hợp đồng qua Đại lý',
  'Stock out Group Agent': 'Giao hợp đồng qua Nhóm đại lý',
  'Stock out Post': 'Giao hợp đồng qua Bưu điện',
  ACK: 'ACK'
}

const mapActions = {
  'Stock in': 'Nhập kho',
  'Change stock-in date': 'Đổi ngày nhập kho',
  'Change office': 'Đổi văn phòng',
  'Give back': 'Trả lại',
  Download: 'Tải về',
  'Deliver contract': 'Giao hợp đồng',
  'Cancel delivery': 'Hủy giao',
  'Change delivery date': 'Điều chỉnh ngày giao',
  Save: 'Lưu',
  Delete: 'Xóa'
}

export const getStatusStyle = (status: string) => {
  switch (status) {
    case 'Còn trong kho':
      return {
        ...statusStyles,
        color: '#008236',
        backgroundColor: '#dcfce7'
      }
    case 'Nhận từ NB':
      return {
        ...statusStyles,
        color: '#1447e6',
        backgroundColor: '#dbeafe'
      }
    case 'Đã giao':
      return {
        ...statusStyles,
        color: '#ca3500',
        backgroundColor: '#ffedd4'
      }
    case 'Đã trả lại':
      return {
        ...statusStyles,
        color: '#000000',
        backgroundColor: '#f6f3f4'
      }
    default:
      return {}
  }
}

export const getPermissions = (): string[] => {
  const authData = pipe(AuthState.auth.get)
  const permissions: string[] = pipe(RBAC.permissionWithNoCache, ErrorHandling.runDidUpdate([authData])) || []
  return permissions
}

export const getStatusLabel = (status: string | null) => {
  return mapStatus[status as keyof typeof mapStatus] || ''
}

export const getScreenLabel = (screen: string | null) => {
  return mapScreen[screen as keyof typeof mapScreen] || ''
}

export const getActionLabel = (action: string | null) => {
  return mapActions[action as keyof typeof mapActions] || ''
}

export const getStatusValue = (label: string) => {
  return Object.keys(mapStatus).find((status) => mapStatus[status as keyof typeof mapStatus] === label) || ''
}

export const parseDateString = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/')
    if (day && month && year) {
      return new Date(Number(year), Number(month) - 1, Number(day))
    }
  }
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? null : date
}

export const isDateAfterString = (date: Date | null, dateStr: string | null | undefined): boolean => {
  if (!date) return false
  if (!dateStr) return true
  const comparisonDate = parseDateString(dateStr)
  if (!comparisonDate) return false

  const dateValue = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const comparisonValue = new Date(
    comparisonDate.getFullYear(),
    comparisonDate.getMonth(),
    comparisonDate.getDate()
  ).getTime()

  return dateValue >= comparisonValue
}

export const isDateAtLeastDaysAfterString = (
  date: Date | null,
  dateStr: string | null | undefined,
  days: number
): boolean => {
  if (!date) return false
  if (!dateStr) return true
  const comparisonDate = parseDateString(dateStr)
  if (!comparisonDate) return false

  const earliestDate = new Date(
    comparisonDate.getFullYear(),
    comparisonDate.getMonth(),
    comparisonDate.getDate() + days
  )
  const dateValue = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

  return dateValue >= earliestDate.getTime()
}

export const getACKStatus = (status: string | null | undefined): string => {
  const mapACKStatus: Record<string, string> = {
    AP: 'Dự thảo',
    CF: 'Hủy hồ sơ yêu cầu BH',
    CP: 'Đã in giấy chứng nhận BH',
    DC: 'Từ chối',
    DE: 'Từ chối yêu cầu giải quyết QLBH',
    DH: 'Yêu cầu quyền lợi tử vong',
    EX: 'Hết hạn',
    FZ: 'Tạm khóa',
    HP: 'Đang chờ khôi phục',
    IF: 'Đang hiệu lực',
    LA: 'Mất hiệu lực',
    MA: 'Đáo hạn',
    MP: 'Điều chỉnh dự thảo',
    PU: 'Đóng đủ phí',
    PW: 'Đang chờ chuyển tiếp',
    RD: 'Đã ghi nhận sự kiện tử vong',
    SU: 'Hủy hợp đồng (nhận giá trị hoàn lại)',
    TD: 'Yêu cầu quyền lợi thương tật toàn bộ và vĩnh viễn',
    TM: 'Chấm dứt hiệu lực do chi trả bồi thường',
    TR: 'Chấm dứt hiệu lực',
    UW: 'Đang thẩm định',
    VO: 'Hủy sản phẩm bổ trợ',
    VR: 'Quyền lợi được ghi nhận',
    WD: 'Hủy theo yêu cầu KH',
    PO: 'Tạm hoãn',
    PS: 'Dự thảo'
  }

  if (!status) return ''
  return mapACKStatus[status] || status
}

export const formatDate = (date: Date | null) => {
  if (!date) {
    return null
  }

  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}
